import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { useUser } from '@/allauth_auth/hooks.js';
import { useOrg } from '@/contexts/OrgContext';
import { usePermissions } from '@/hooks/usePermissions';
import { Toggle } from '@/lib/base/forms/toggle';
import { createEventBuilder } from '@/lib/crm/core/EventBuilder';
import type { EntityTypeName } from '@/lib/crm/core/types';
import { useSubmitEvents } from '@/lib/crm/query/mutations';
import { useCrmObject } from '@/lib/crm/store';
import { logger } from '@/lib/logger';
import { useViewHealthMarker } from '@/lib/view-health';
import type { CrmObject } from '@/types/crm';

import { DefaultTimezoneCard } from './components/DefaultTimezoneCard';
import { CurrencyCard } from './currency-section';
import { SettingsCard } from './SettingsCard';
import { useMountedOrgGuard } from './use-mounted-org-guard';

// ---------------------------------------------------------------------------
// Module-scope constants
// ---------------------------------------------------------------------------

const AUTO_CREATE_COMPANIES_PROPERTY = 'ahoy_auto_create_companies';

// ---------------------------------------------------------------------------
// Helper
// ---------------------------------------------------------------------------

function deriveEnabled(object: CrmObject | undefined): boolean {
  const raw = object?.properties?.[AUTO_CREATE_COMPANIES_PROPERTY]?.value;
  if (raw === undefined || raw === null) {
    return true;
  }
  // Normalize string values (trim + case-fold) before comparing so a
  // whitespace-padded or case-variant 'false' is read as disabled, matching
  // how the backend interprets the stored value.
  if (typeof raw === 'string') {
    return raw.trim().toLowerCase() !== 'false';
  }
  return raw !== false;
}

// ---------------------------------------------------------------------------
// AutoCreateCompaniesCard — internal, not exported
// ---------------------------------------------------------------------------

function AutoCreateCompaniesCard() {
  const { currentOrg } = useOrg();
  const { hasPermission } = usePermissions();
  // Derive a plain boolean during render — hasPermission has unstable identity
  // and must never enter a dep array.
  const canEdit = hasPermission('ahoy_org', 'ahoy_property_updated');
  const user = useUser();
  const orgEntity = useCrmObject(
    currentOrg?.id ?? 0,
    'ahoy_org' as EntityTypeName,
    currentOrg?.entityId ?? undefined
  );
  const { mutateAsync: submitEvents, isPending } = useSubmitEvents(
    currentOrg?.id ?? 0,
    'ahoy_org' as EntityTypeName
  );

  const savedEnabled = useMemo(
    () => deriveEnabled(orgEntity.object),
    [orgEntity.object]
  );

  const [pendingValue, setPendingValue] = useState<boolean | null>(null);
  const { isMountedRef, currentOrgIdRef } = useMountedOrgGuard(currentOrg?.id);

  const displayed = pendingValue ?? savedEnabled;

  // Reset the optimistic pending value whenever the org changes — otherwise an
  // in-flight save's pending value can leak into the next org and override its
  // saved setting.
  useEffect(() => {
    setPendingValue(null);
  }, [currentOrg?.id]);

  // Clear the optimistic pending value only after the write completes and the
  // saved value reflects the last write — gated on isPending so a stale
  // refetch from a superseded write doesn't clear a newer pending value.
  useEffect(() => {
    if (!isPending && pendingValue !== null && savedEnabled === pendingValue) {
      setPendingValue(null);
    }
  }, [savedEnabled, pendingValue, isPending]);

  const handleToggle = useCallback(
    async (next: boolean) => {
      if (!canEdit || !orgEntity.object) {
        return;
      }
      const userId = user?.id;
      if (userId == null || userId === '') {
        toast.error('You must be signed in to change this setting');
        return;
      }
      const requestOrgId = currentOrg?.id;
      setPendingValue(next);
      try {
        const builder = createEventBuilder({
          sourceType: 'ahoy_user',
          sourceId: String(userId),
          startSeq: orgEntity.object.last_event_seq,
        });
        builder.propertiesUpdated(orgEntity.object.entity_id, 'ahoy_org', {
          [AUTO_CREATE_COMPANIES_PROPERTY]: next ? 'true' : 'false',
        });
        await submitEvents({ events: builder.build() });
        // Only surface success if the component is still mounted and the user
        // is still on the org that started the request — a save that lands
        // after unmount or an org switch must not call setState / toast
        // against the newly selected org.
        if (isMountedRef.current && currentOrgIdRef.current === requestOrgId) {
          toast.success('Setting saved');
        }
      } catch (err) {
        logger.error(
          'Failed to save auto-create-companies setting',
          err instanceof Error ? err : new Error(String(err)),
          { org_id: requestOrgId }
        );
        // Gate revert + error feedback by mount state and the request's org
        // id so a failed save for a since-unmounted component or a
        // since-abandoned org doesn't clear the new org's pending value or
        // surface an error against it.
        if (isMountedRef.current && currentOrgIdRef.current === requestOrgId) {
          setPendingValue(null);
          toast.error("We couldn't save this setting. Try again.");
        }
      }
    },
    [
      orgEntity.object,
      submitEvents,
      user,
      currentOrg?.id,
      canEdit,
      isMountedRef,
      currentOrgIdRef,
    ]
  );

  // --- Conditional returns after all hooks ---
  if (!currentOrg?.entityId) {
    return null;
  }

  if (orgEntity.error) {
    return (
      <SettingsCard>
        <p className='text-sm font-medium text-fg-primary'>
          Automatically create companies for contacts
        </p>
        <p className='text-sm text-error-primary'>
          Couldn&apos;t load this setting. Refresh to try again.
        </p>
      </SettingsCard>
    );
  }

  // Treat an unresolved object (no error, not flagged loading) as still
  // unavailable — otherwise this branch would render a functional-looking
  // switch that silently no-ops, since handleToggle returns on
  // `!orgEntity.object`.
  if (orgEntity.isLoading || !orgEntity.object) {
    return (
      <SettingsCard>
        <p className='text-sm font-medium text-fg-primary'>
          Automatically create companies for contacts
        </p>
        <p className='text-sm text-fg-tertiary'>Loading&hellip;</p>
      </SettingsCard>
    );
  }

  return (
    <SettingsCard>
      <div className='flex items-start justify-between gap-4'>
        <div className='min-w-0 flex-1'>
          <p className='text-sm font-medium text-fg-primary'>
            Automatically create companies for contacts
          </p>
          <p
            id='auto-create-companies-hint'
            className='mt-0.5 text-sm text-fg-tertiary'
          >
            When on, Ahoy creates a company for a contact&apos;s email domain if
            one doesn&apos;t already exist. Turning this off keeps linking
            contacts to companies that already exist.
          </p>
          {!canEdit && (
            <p className='mt-2 text-sm text-fg-tertiary'>
              Only account admins can change this setting.
            </p>
          )}
        </div>
        <Toggle
          size='md'
          isSelected={displayed}
          onChange={handleToggle}
          // Stay disabled until the canonical object reflects the pending
          // value (pendingValue clears). isPending drops when the write
          // settles, but the store may not yet show the saved value — a
          // second click in that gap would submit from a stale
          // last_event_seq and conflict or revert the newer choice.
          isDisabled={!canEdit || isPending || pendingValue !== null}
          aria-label='Automatically create companies for contacts'
          aria-describedby='auto-create-companies-hint'
          className='shrink-0'
        />
      </div>
    </SettingsCard>
  );
}

// ---------------------------------------------------------------------------
// CrmDefaultsSection — exported section shell
// ---------------------------------------------------------------------------

export function CrmDefaultsSection() {
  const { currentOrg } = useOrg();
  const orgEntity = useCrmObject(
    currentOrg?.id ?? 0,
    'ahoy_org' as EntityTypeName,
    currentOrg?.entityId ?? undefined
  );

  useViewHealthMarker('settings-section-ready', {
    ready: Boolean(orgEntity.object),
  });

  return (
    <div className='space-y-8'>
      <div>
        <h2 className='text-lg font-semibold text-fg-primary'>CRM defaults</h2>
        <p className='mt-0.5 text-sm text-fg-tertiary'>
          These settings apply to everyone in your workspace.
        </p>
      </div>
      <AutoCreateCompaniesCard />
      <CurrencyCard />
      <DefaultTimezoneCard />
    </div>
  );
}
